/**
 * The training nudge at the top of the queue shows once per browser (minimal redesign,
 * 12 Oct 2026). The first queue visit writes `i2q.training.nudge.seen`; every later
 * visit reads it and leaves the nudge out, whatever the training progress says. The
 * training pages stay one click away in the menu. Storage can be unavailable (private
 * mode, blocked): every access is guarded, and without it the nudge comes back.
 */

import { readRemembered, remember } from "@/lib/remembered";

export const NUDGE_SEEN_KEY = "i2q.training.nudge.seen";

/**
 * The start hint over the queue (ease round, 13 Oct 2026): "New here? Start with Ready to
 * quote." It shows once per browser, on the tab "To review", while that tab's sibling
 * "Ready to quote" holds a case. "Show them" and "Not now" both write the key.
 */
export const START_HINT_SEEN_KEY = "i2q.queue.start.hint.seen";

/** True when this browser has answered the start hint before. */
export function startHintSeen(): boolean {
  return readRemembered(START_HINT_SEEN_KEY, "") === "1";
}

/** Remember that the start hint was answered. */
export function markStartHintSeen(): void {
  remember(START_HINT_SEEN_KEY, "1");
}

/**
 * Whether the start hint shows: only on "To review", only while "Ready to quote" has a
 * case under the current filters, and only until the person answered it once.
 */
export function showsStartHint(facts: {
  view: string;
  readyCount: number | null | undefined;
  seen: boolean;
}): boolean {
  return facts.view === "work" && !facts.seen && (facts.readyCount ?? 0) > 0;
}

/** True when this browser has shown the nudge before. */
export function nudgeSeenBefore(): boolean {
  try {
    return window.localStorage.getItem(NUDGE_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

/** Remember that the nudge was shown. Writing the same value again changes nothing. */
export function markNudgeSeen(): void {
  try {
    window.localStorage.setItem(NUDGE_SEEN_KEY, "1");
  } catch {
    // No storage in this browser: the nudge shows again on the next visit.
  }
}
